import React, { useState, useEffect, useCallback } from 'react';
import { Download, FileText, Calendar, User, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmtRound = (val) => Math.round(parseFloat(val || 0)).toLocaleString('en-IN');
const monthLabel = (m) => {
  if (!m) return '';
  const d = new Date(m + '-01');
  return `${d.toLocaleString('en-IN', { month: 'long' })}, ${m.split('-')[0]}`;
};

const ShiftProSalarySlip = ({ user }) => {
  const [employeeCode, setEmployeeCode] = useState('');
  const [verified, setVerified] = useState(false);
  const [salary, setSalary] = useState(null);
  const [allMonths, setAllMonths] = useState([]);
  const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Fetch all months for employee to show available months
  const fetchAllMonths = useCallback(async (code) => {
    try {
      const res = await api.get(`/employee-salary-slips?employee_code=${code}`);
      const data = Array.isArray(res.data) ? res.data : [];
      const months = [...new Set(data.map(d => d.month))].sort().reverse();
      setAllMonths(months);
      if (months.length > 0 && !months.includes(filterMonth)) {
        setFilterMonth(months[0]);
      }
    } catch { /* ignore */ }
  }, [filterMonth]);

  const fetchSalary = useCallback(async () => {
    if (!employeeCode || !verified) return;
    setLoading(true);
    try {
      const res = await api.get(`/employee-salary-slips?employee_code=${employeeCode}&month=${filterMonth}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setSalary(data.length > 0 ? data[0] : null);
    } catch {
      toast.error('Failed to fetch salary data');
      setSalary(null);
    } finally { setLoading(false); }
  }, [employeeCode, filterMonth, verified]);

  useEffect(() => { if (verified) fetchSalary(); }, [fetchSalary, verified]);

  const handleVerify = async () => {
    if (!employeeCode.trim()) { toast.error('Enter your employee code'); return; }
    setLoading(true);
    try {
      const res = await api.get(`/employee-salary-slips?employee_code=${employeeCode.trim()}`);
      const data = Array.isArray(res.data) ? res.data : [];
      if (data.length > 0) {
        setVerified(true);
        const months = [...new Set(data.map(d => d.month))].sort().reverse();
        setAllMonths(months);
        if (months.length > 0) setFilterMonth(months[0]);
        toast.success(`Found ${data.length} salary record(s)`);
      } else {
        toast.error('No approved salary records found for this employee code');
      }
    } catch {
      toast.error('Failed to verify employee code');
    } finally { setLoading(false); }
  };

  const handleDownload = async () => {
    if (!salary) return;
    setDownloading(true);
    try {
      const response = await api.post('/salary-slip/generate', { salary_ids: [salary.id] }, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Salary_Slip_${salary.employee_name}_${filterMonth}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Salary slip downloaded');
    } catch {
      toast.error('Failed to generate salary slip');
    } finally { setDownloading(false); }
  };

  // Not verified — show code entry
  if (!verified) {
    return (
      <div className="space-y-5" data-testid="shiftpro-salary-slip-page">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">My Salary Slip</h1>
          <p className="mt-1 text-base text-slate-600">Enter your employee code to view and download salary slips</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm max-w-md mx-auto p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
              <User className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          <div className="text-center mb-6">
            <h2 className="text-lg font-bold text-slate-900">Employee Verification</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your employee code to access your salary slips</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Employee Code</Label>
              <Input
                value={employeeCode}
                onChange={e => setEmployeeCode(e.target.value.toUpperCase())}
                placeholder="e.g. EMP0012"
                className="mt-1 text-center text-lg font-mono tracking-widest"
                onKeyDown={e => e.key === 'Enter' && handleVerify()}
                data-testid="emp-code-input"
              />
            </div>
            <Button onClick={handleVerify} disabled={loading} className="w-full" data-testid="verify-btn">
              {loading ? 'Verifying...' : 'View My Salary Slips'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="shiftpro-salary-slip-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">My Salary Slip</h1>
          <p className="mt-1 text-base text-slate-600">
            Employee: <span className="font-semibold text-indigo-600">{employeeCode}</span>
            {salary && <span className="ml-2">— {salary.employee_name}</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setVerified(false); setSalary(null); setAllMonths([]); }} data-testid="change-code-btn">
          Change Employee
        </Button>
      </div>

      {/* Month selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex items-end gap-4 flex-wrap">
        <div>
          <Label className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> Select Month</Label>
          <Input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)} className="mt-1 w-44" data-testid="month-picker" />
        </div>
        {allMonths.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Available:</span>
            {allMonths.map(m => (
              <button key={m} onClick={() => setFilterMonth(m)}
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                  m === filterMonth ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`} data-testid={`month-btn-${m}`}>
                {monthLabel(m)}
              </button>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-900"></div></div>
      ) : !salary ? (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center shadow-sm">
          <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900">No Salary Slip</h3>
          <p className="text-slate-500 mt-2">No approved salary record found for {monthLabel(filterMonth)}.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="salary-slip-detail">
          {/* Header */}
          <div className="px-6 py-4 bg-slate-800 text-white flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold">{salary.employee_name}</h2>
              <p className="text-sm text-slate-300">{monthLabel(salary.month)}</p>
            </div>
            <Button onClick={handleDownload} disabled={downloading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="download-btn">
              <Download className="h-4 w-4 mr-1.5" />
              {downloading ? 'Generating...' : 'Download PDF'}
            </Button>
          </div>

          {/* Salary details */}
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                <p className="text-[10px] text-emerald-600 uppercase font-semibold mb-1">Gross Salary</p>
                <p className="text-xl font-bold text-emerald-800">{fmtRound(salary.gross_salary)}</p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                <p className="text-[10px] text-blue-600 uppercase font-semibold mb-1">Earned Salary</p>
                <p className="text-xl font-bold text-blue-800">{fmtRound(salary.earned_salary || salary.gross_salary)}</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                <p className="text-[10px] text-red-600 uppercase font-semibold mb-1">Total Deductions</p>
                <p className="text-xl font-bold text-red-800">{fmtRound(salary.total_deductions)}</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-center">
                <p className="text-[10px] text-indigo-600 uppercase font-semibold mb-1">Net Salary</p>
                <p className="text-2xl font-bold text-indigo-800">{fmtRound(salary.net_salary)}</p>
              </div>
            </div>

            {/* Earnings & Deductions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Earnings */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-emerald-800 text-white text-xs font-semibold uppercase">Earnings</div>
                <div className="divide-y divide-slate-100">
                  {[
                    ['Basic', salary.basic],
                    ['DA', salary.da],
                    ['HRA', salary.hra],
                    ['Conveyance', salary.conveyance_allowance],
                    ['Medical', salary.medical_allowance],
                    ['Other Allowance', salary.other_allowance],
                    ['OT/ODW Amount', (salary.ot_amount || 0) + (salary.off_day_amount || 0)],
                  ].filter(([, v]) => v > 0).map(([label, val]) => (
                    <div key={label} className="flex justify-between px-4 py-2 text-xs">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-mono font-semibold text-slate-900">{fmtRound(val)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between px-4 py-2.5 bg-emerald-50 text-xs font-bold">
                    <span>Total Earnings</span>
                    <span className="font-mono">{fmtRound(salary.earned_salary || salary.gross_salary)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 bg-red-800 text-white text-xs font-semibold uppercase">Deductions</div>
                <div className="divide-y divide-slate-100">
                  {[
                    ['PF', salary.pf],
                    ['ESI/ESIC', salary.esi || salary.esic],
                    ['Professional Tax', salary.professional_tax],
                    ['MLWF', salary.mlwf],
                    ['TDS', salary.tds],
                    ['Advance', salary.advance_deduction],
                    ['Other Deductions', salary.other_deductions],
                  ].filter(([, v]) => v > 0).map(([label, val]) => (
                    <div key={label} className="flex justify-between px-4 py-2 text-xs">
                      <span className="text-slate-600">{label}</span>
                      <span className="font-mono font-semibold text-red-700">{fmtRound(val)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between px-4 py-2.5 bg-red-50 text-xs font-bold">
                    <span>Total Deductions</span>
                    <span className="font-mono text-red-700">{fmtRound(salary.total_deductions)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Attendance Summary */}
            <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 bg-slate-700 text-white text-xs font-semibold uppercase">Attendance Summary</div>
              <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y divide-slate-100">
                {[
                  ['Days Present', salary.days_present || salary.paid_days || 0],
                  ['Weekly Offs', salary.weekly_offs || 0],
                  ['Paid Leaves', salary.paid_leaves || 0],
                  ['LWP', salary.lwp || 0],
                  ['Total Days', salary.total_days || salary.calendar_days || 0],
                ].map(([label, val]) => (
                  <div key={label} className="px-4 py-3 text-center">
                    <p className="text-[10px] text-slate-500 uppercase font-medium">{label}</p>
                    <p className="text-lg font-bold text-slate-900 font-mono">{val}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Net Pay highlight */}
            <div className="mt-6 bg-indigo-900 text-white rounded-xl px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <IndianRupee className="h-8 w-8 opacity-50" />
                <div>
                  <p className="text-xs text-indigo-300 uppercase font-semibold">Net Pay</p>
                  <p className="text-3xl font-bold">{fmtRound(salary.net_salary)}</p>
                </div>
              </div>
              <Button onClick={handleDownload} disabled={downloading} variant="secondary" data-testid="download-btn-bottom">
                <Download className="h-4 w-4 mr-1.5" />
                {downloading ? 'Generating...' : 'Download PDF'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftProSalarySlip;
